import React, { useState, useEffect, useRef } from 'react';
import { track, getSlug } from '@/utils/tracker';
import { useParams, Link } from 'react-router-dom';
import ChroLayout from '@/components/layouts/ChroLayout';
import { chroApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2,
  Bot,
  CheckCircle,
  Clock,
  X,
  ExternalLink,
  Shield,
  Zap,
  ChevronLeft,
} from 'lucide-react';

const formatCurrency = (value, currency = 'GBP') => {
  const symbols = { GBP: '£', USD: '$', AED: 'AED ', INR: '₹', MUR: 'Rs ' };
  const symbol = symbols[currency] || '£';
  if (value >= 1000) return `${symbol}${(value / 1000).toFixed(0)}K`;
  return `${symbol}${value?.toFixed(0) || 0}`;
};

const Showcase = () => {
  const { blueprintId } = useParams();
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [blueprint, setBlueprint] = useState(null);

  const _trackSlug = getSlug();
  const _trackEntry = useRef(Date.now());

  useEffect(() => {
    track(_trackSlug, 'screen_viewed', 'showcase');
    return () => {
      const dur = Math.round((Date.now() - _trackEntry.current) / 1000);
      track(_trackSlug, 'screen_exited', 'showcase', dur);
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [blueprintId]);

  const fetchData = async () => {
    if (localStorage.getItem("is_demo") === "true") { setLoading(false); return; }
    try {
      const [recsRes, bpRes] = await Promise.all([
        chroApi.getShowcaseRecommendations(blueprintId),
        chroApi.getBlueprint(blueprintId)
      ]);
      setRecommendations(recsRes.data);
      setBlueprint(bpRes.data);
    } catch (error) {
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (recommendationId, action, toolId) => {
    try {
      await chroApi.actionToolRecommendation(recommendationId, action, toolId);
      toast.success(`Tool ${action}ed`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update tool');
    }
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'low': return 'bg-accent/20 text-accent';
      case 'medium': return 'bg-warning/20 text-warning';
      case 'high': return 'bg-destructive/20 text-destructive';
      default: return '';
    }
  };

  if (loading) {
    return (
      <ChroLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ChroLayout>
    );
  }

  if (recommendations.length === 0) {
    return (
      <ChroLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button variant="ghost" asChild className="mb-6">
            <Link to="/blueprint">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Blueprint
            </Link>
          </Button>
          
          <Card className="glass-light">
            <CardContent className="p-12 text-center">
              <Bot className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No AI-Addressable Clusters</h3>
              <p className="text-muted-foreground">
                This Blueprint did not identify any AI-addressable task clusters.
                All tasks are best suited for human workforce types.
              </p>
            </CardContent>
          </Card>
        </div>
      </ChroLayout>
    );
  }

  const totalPotentialSavings = recommendations.reduce((sum, rec) => {
    return sum + (rec.tools?.reduce((tSum, t) => tSum + (t.estimated_annual_saving || 0), 0) || 0) / 3;
  }, 0);

  return (
    <ChroLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Button variant="ghost" asChild className="mb-6">
          <Link to="/blueprint">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Blueprint
          </Link>
        </Button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">The Showcase</h1>
            <p className="text-muted-foreground">
              AI tool recommendations for your Blueprint task clusters
            </p>
          </div>
          <Card className="glass-light">
            <CardContent className="px-6 py-4">
              <p className="text-sm text-muted-foreground">Potential Annual Savings</p>
              <p className="text-2xl font-bold gradient-text">
                {formatCurrency(totalPotentialSavings)}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-8">
          {recommendations.map((rec) => (
            <Card key={rec.id} className="glass-light">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Bot className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{rec.task_cluster}</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {rec.tools?.length || 0} tools shortlisted
                      </p>
                    </div>
                  </div>
                  {rec.action && (
                    <Badge variant={rec.action === 'shortlist' ? 'default' : 'outline'}>
                      {rec.action === 'shortlist' ? 'Shortlisted' : 
                       rec.action === 'defer' ? 'Deferred' : 'Skipped'}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {rec.tools?.map((tool, index) => (
                    <Card 
                      key={tool.id || index}
                      className={`bg-background/50 ${
                        rec.selected_tool_id === tool.id ? 'ring-2 ring-primary' : ''
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-semibold">{tool.name}</h4>
                          {tool.website_url && (
                            <a 
                              href={tool.website_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-muted-foreground hover:text-primary"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {tool.description}
                        </p>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Est. Savings</span>
                            <span className="font-medium text-accent">
                              {formatCurrency(tool.estimated_annual_saving)}/yr
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Complexity</span>
                            <Badge className={getComplexityColor(tool.implementation_complexity)}>
                              {tool.implementation_complexity}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Pricing</span>
                            <span className="capitalize">{tool.pricing_model?.replace('_', ' ')}</span>
                          </div>
                        </div>
                        
                        {tool.security_badges?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-4">
                            {tool.security_badges.map((badge) => (
                              <Badge key={badge} variant="outline" className="text-xs">
                                <Shield className="h-3 w-3 mr-1" />
                                {badge}
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        {!rec.action && (
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              className="flex-1"
                              onClick={() => handleAction(rec.id, 'shortlist', tool.id)}
                              data-testid={`shortlist-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Shortlist
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleAction(rec.id, 'defer', tool.id)}
                            >
                              <Clock className="h-3 w-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleAction(rec.id, 'skip', tool.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info Card */}
        <Card className="mt-8 glass-heavy border-primary/20">
          <CardContent className="p-6 flex items-start gap-4">
            <Zap className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold mb-1">Zero Cost to You</h3>
              <p className="text-sm text-muted-foreground">
                Tool recommendations are provided at no cost. Shortlisted tools appear in your 
                Blueprint Procurement Plan. Vendor referral fees are handled separately.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </ChroLayout>
  );
};

export default Showcase;
